import { useState } from "react";
import { useNavigate } from "react-router-dom";
import WorkspaceShell from "@/components/WorkspaceShell";
import ChatPane from "@/components/ChatPane";
import { productByKey } from "@/lib/products";
import api from "@/lib/api";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { ArrowRight, Loader2, Code2, MessageSquare, CheckCircle2, GitCompare, Repeat, Play, BookOpen } from "lucide-react";
import { SymbolicPanel, ConvertPanel, SandboxPanel, RecipesPanel } from "@/components/mizan/MizanPanels";

const PRODUCT = productByKey("mizan");

const STARTERS = [
  "Design a PID controller in Python for a 2nd-order plant with damping 0.3 and natural frequency 5 rad/s.",
  "How do I compute an FFT and plot its magnitude spectrum with numpy + matplotlib?",
  "Convert a Simulink-style state-space simulation to Python with scipy.signal.",
  "Solve a 1D heat equation with finite differences in Python.",
];

const SAMPLE_MATLAB = `% Step response of a damped 2nd-order system
zeta = 0.3;
wn = 5;
G = tf(wn^2, [1, 2*zeta*wn, wn^2]);
t = 0:0.01:5;
y = step(G, t);
plot(t, y);
xlabel('Time (s)');
ylabel('Response');
title('Step response');
grid on;`;

export default function Mizan() {
  const [tab, setTab] = useState("transpile");
  const [matlab, setMatlab] = useState(SAMPLE_MATLAB);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bump, setBump] = useState(0);
  const nav = useNavigate();

  const transpile = async () => {
    if (!matlab.trim() || loading) return;
    setLoading(true);
    try {
      const r = await api.post("/mizan/transpile", { matlab_code: matlab });
      setResult(r.data);
      setBump((n) => n + 1);
      toast.success("Transpilation complete");
    } catch (e) {
      if (!handleQuotaError(e, nav)) {
        toast.error(e?.response?.data?.detail || "Transpile failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceShell product={PRODUCT} usageRefresh={bump}>
      <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
        <TabButton active={tab === "transpile"} onClick={() => setTab("transpile")} icon={Code2} label="Transpiler" testid="mizan-tab-transpile" />
        <TabButton active={tab === "verify"} onClick={() => setTab("verify")} icon={GitCompare} label="Verify" testid="mizan-tab-verify" />
        <TabButton active={tab === "convert"} onClick={() => setTab("convert")} icon={Repeat} label="Convert" testid="mizan-tab-convert" />
        <TabButton active={tab === "sandbox"} onClick={() => setTab("sandbox")} icon={Play} label="Sandbox" testid="mizan-tab-sandbox" />
        <TabButton active={tab === "recipes"} onClick={() => setTab("recipes")} icon={BookOpen} label="Recipes" testid="mizan-tab-recipes" />
        <TabButton active={tab === "chat"} onClick={() => setTab("chat")} icon={MessageSquare} label="Co-pilot" testid="mizan-tab-chat" />
      </div>

      {tab === "transpile" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-sm border border-border/60 bg-card overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 border-b border-border/60 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">MATLAB</div>
              <button
                onClick={() => setMatlab(SAMPLE_MATLAB)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Load sample
              </button>
            </div>
            <textarea
              data-testid="mizan-matlab"
              value={matlab}
              onChange={(e) => setMatlab(e.target.value)}
              rows={20}
              spellCheck={false}
              className="flex-1 w-full p-4 font-mono text-[13px] bg-background outline-none resize-none border-0"
              placeholder="Paste MATLAB code here…"
            />
            <div className="p-3 border-t border-border/60">
              <button
                data-testid="mizan-transpile"
                onClick={transpile}
                disabled={loading || !matlab.trim()}
                className="inline-flex w-full h-11 items-center justify-center gap-2 rounded-sm text-white text-sm font-medium disabled:opacity-50"
                style={{ background: PRODUCT.gradient }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Transpile to Python
              </button>
            </div>
          </div>

          <div className="rounded-sm border border-border/60 bg-card overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 border-b border-border/60 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Python</div>
              {result?.equivalent && (
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em]" style={{ color: PRODUCT.accent }}>
                  <CheckCircle2 className="h-3 w-3" /> Verified equivalent
                </span>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              {result ? (
                <pre className="p-4 font-mono text-[13px] whitespace-pre-wrap">{result.python_code}</pre>
              ) : (
                <div className="h-full flex items-center justify-center p-6 text-center text-muted-foreground text-sm">
                  Click <span className="mx-1 px-1.5 py-0.5 bg-secondary rounded-sm text-foreground text-xs">Transpile</span> to generate clean Python equivalent.
                </div>
              )}
            </div>
            {result?.explanation && (
              <div className="border-t border-border/60 p-4 bg-secondary/40">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1.5">Explanation</div>
                <p className="text-sm leading-relaxed">{result.explanation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "verify" && <SymbolicPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}
      {tab === "convert" && <ConvertPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}
      {tab === "sandbox" && <SandboxPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}
      {tab === "recipes" && <RecipesPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}

      {tab === "chat" && (
        <ChatPane
          product={PRODUCT}
          endpoint="/mizan/chat"
          accent={PRODUCT.accent}
          placeholder="Ask Mizan an engineering or numerical question…"
          starters={STARTERS}
          stream
          streamProduct="mizan"
          onSent={() => setBump((n) => n + 1)}
        />
      )}
    </WorkspaceShell>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex h-9 items-center gap-2 rounded-sm px-3.5 text-sm font-medium transition-colors ${
        active
          ? "bg-foreground text-background"
          : "bg-card border border-border/60 text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
